import { useEffect, useState } from "react"

export type PresenceState = 'entering' | 'present' | 'leaving'

export type PresenceEntry<T> = {
  key: string
  item: T
  state: PresenceState
}

// how long an entering item sits in its start state so the browser paints it before transitioning in
const ENTER_DELAY_MS = 30

// keeps removed items around for exitMs so they can animate out, and flags new items as entering so they can animate in
export const usePresence = <T,>(items: T[], getKey: (item: T) => string, exitMs: number): PresenceEntry<T>[] => {
  const [entries, setEntries] = useState<PresenceEntry<T>[]>(() =>
    items.map((item) => ({ key: getKey(item), item, state: 'present' })),
  )

  const keySignature = items.map(getKey).join('|')

  useEffect(() => {
    setEntries((previousEntries) => {
      const previousByKey = new Map(previousEntries.map((entry) => [entry.key, entry]))
      const nextKeys = new Set(items.map(getKey))

      const nextEntries: PresenceEntry<T>[] = items.map((item) => {
        const key = getKey(item)
        const previous = previousByKey.get(key)
        if (!previous) return { key, item, state: 'entering' }
        return { key, item, state: previous.state === 'leaving' ? 'present' : previous.state }
      })

      // leaving items keep their last snapshot and roughly their old position
      previousEntries.forEach((entry, index) => {
        if (nextKeys.has(entry.key)) return
        nextEntries.splice(Math.min(index, nextEntries.length), 0, { ...entry, state: 'leaving' })
      })

      return nextEntries
    })
    // the key signature captures every change that adds or removes an item
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature])

  useEffect(() => {
    const enteringKeys = entries.filter((entry) => entry.state === 'entering').map((entry) => entry.key)
    if (enteringKeys.length === 0) return
    const timeout = setTimeout(() => {
      setEntries((current) => current.map((entry) =>
        enteringKeys.includes(entry.key) && entry.state === 'entering' ? { ...entry, state: 'present' } : entry,
      ))
    }, ENTER_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [entries])

  useEffect(() => {
    const leavingKeys = entries.filter((entry) => entry.state === 'leaving').map((entry) => entry.key)
    if (leavingKeys.length === 0) return
    const timeout = setTimeout(() => {
      setEntries((current) => current.filter((entry) => !(leavingKeys.includes(entry.key) && entry.state === 'leaving')))
    }, exitMs)
    return () => clearTimeout(timeout)
  }, [entries, exitMs])

  // present items always render their latest data, only leaving items use the snapshot
  const latestByKey = new Map(items.map((item) => [getKey(item), item]))
  return entries.map((entry) => entry.state === 'leaving' ? entry : { ...entry, item: latestByKey.get(entry.key) ?? entry.item })
}
